import React from "react";
import { MapPin, Phone, MessageCircle, Instagram, Facebook, Mail, Clock } from "lucide-react";
import { trackEvent } from "../../lib/analytics";

const clinics = [
  {
    name: "Madinaguda Clinic",
    address: "H.No. 7‑1/2, Above New Reliance Car Decors, Sri Durga Colony, Madinaguda Main Road, Hyderabad, Telangana 500049",
    hours: "Mon – Sat · 11 AM – 3:30 PM",
    mapQ: "Asian+Diabetes+Thyroid+Hormone+Superspeciality+Centre+Madinaguda+Hyderabad",
    dirs: "https://share.google/dAok7dSYQwYRhF7n5",
  },
];

export const Footer = () => {
  return (
    <footer className="bg-[#242220] text-[#E8F0E9] pt-20 pb-10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Clinic map */}
        <div className="grid grid-cols-1 gap-6 mb-16">
          {clinics.map((c, i) => (
            <div
              key={c.name}
              data-testid={`footer-clinic-${i}`}
              className="rounded-[32px] overflow-hidden border border-white/10 bg-white/[0.04] grid grid-cols-1 md:grid-cols-2"
            >
              <div className="aspect-[16/10] md:aspect-auto min-h-[280px] w-full">
                <iframe
                  data-testid={`footer-map-${i}`}
                  title={c.name}
                  src={`https://www.google.com/maps?q=${c.mapQ}&output=embed`}
                  width="100%"
                  height="100%"
                  style={{ border: 0, display: "block" }}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
              <div className="p-8 md:p-10 flex flex-col justify-center">
                <p className="inline-flex items-center gap-2 font-micro text-[#F5A896]">
                  <MapPin className="h-3.5 w-3.5" /> {c.name}
                </p>
                <p className="font-display text-2xl text-white mt-3 leading-snug">{c.address}</p>
                <p className="mt-3 flex items-center gap-2 text-sm text-[#E8F0E9]/80">
                  <Clock className="h-4 w-4 text-[#EB8771]" strokeWidth={1.7} /> {c.hours}
                </p>
                <a
                  data-testid={`footer-directions-${i}`}
                  href={c.dirs}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => trackEvent("directions_clicked", { location: "footer", clinic: c.name })}
                  className="mt-6 inline-flex self-start items-center gap-2 rounded-full bg-white text-[#242220] hover:bg-[#E8F0E9] px-5 py-2.5 text-sm font-medium transition-all hover:scale-[1.03]"
                >
                  <MapPin className="h-4 w-4" /> Get Directions
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Identity / contact / social */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          <div>
            <p className="font-display text-3xl text-white">Dr. Shalini Patlolla</p>
            <p className="mt-2 text-sm italic text-[#F5A896]">
              Senior Endocrinologist · MD, DM (SVIMS) · Founder
            </p>
            <p className="mt-6 text-sm leading-relaxed text-[#E8F0E9]/75 max-w-sm">
              Asian Diabetes Thyroid Hormone Superspeciality Centre — evidence‑led care for
              diabetes, thyroid, PCOD, obesity and medical weight loss.
            </p>
            <div className="mt-5 flex items-center gap-3">
              <a
                data-testid="social-ig"
                href="https://www.instagram.com/asiandiabetesthyroidhormone"
                target="_blank"
                rel="noreferrer"
                aria-label="Instagram — Asian Diabetes Thyroid Hormone"
                className="h-9 w-9 grid place-items-center rounded-full bg-white/5 hover:bg-[#EB8771] transition-colors"
              >
                <Instagram className="h-4 w-4" />
              </a>
              <a
                data-testid="social-fb"
                href="https://www.asiandiabeticcenter.in/"
                target="_blank"
                rel="noreferrer"
                aria-label="Website — asiandiabeticcenter.in"
                className="h-9 w-9 grid place-items-center rounded-full bg-white/5 hover:bg-[#EB8771] transition-colors"
              >
                <Facebook className="h-4 w-4" />
              </a>
            </div>
          </div>

          <div>
            <p className="text-[11px] tracking-[0.2em] uppercase text-[#F5A896]">Talk</p>
            <div className="mt-4 space-y-3 text-sm">
              <a data-testid="footer-call-1" href="tel:+919133552279" onClick={() => trackEvent("call_clicked", { location: "footer", number: "primary" })} className="flex items-center gap-3 hover:text-white">
                <Phone className="h-4 w-4 text-[#EB8771]" strokeWidth={1.7} /> +91 91335 52279
              </a>
              <a data-testid="footer-call-2" href="tel:+919000030709" onClick={() => trackEvent("call_clicked", { location: "footer", number: "secondary" })} className="flex items-center gap-3 hover:text-white">
                <Phone className="h-4 w-4 text-[#EB8771]" strokeWidth={1.7} /> +91 90000 30709
              </a>
              <a
                data-testid="footer-whatsapp"
                href="https://wa.me/919133552279"
                target="_blank"
                rel="noreferrer"
                onClick={() => trackEvent("whatsapp_clicked", { location: "footer" })}
                className="flex items-center gap-3 hover:text-white"
              >
                <MessageCircle className="h-4 w-4 text-[#EB8771]" strokeWidth={1.7} /> WhatsApp the clinic
              </a>
              <a
                data-testid="footer-email"
                href="mailto:drshaliniendocrine@gmail.com"
                className="flex items-center gap-3 hover:text-white"
              >
                <Mail className="h-4 w-4 text-[#EB8771]" strokeWidth={1.7} /> drshaliniendocrine@gmail.com
              </a>
            </div>
          </div>

          <div>
            <p className="text-[11px] tracking-[0.2em] uppercase text-[#F5A896]">Clinic</p>
            <p className="mt-4 text-sm text-[#E8F0E9]/80">
              Asian Diabetes Thyroid Hormone Superspeciality Centre
            </p>
            <p className="mt-2 text-sm text-[#E8F0E9]/60">
              Madinaguda · Hyderabad
            </p>
            <a
              href="https://book.healthplix.com/dr-dr-p-shalini-general-physician-hyderabad"
              target="_blank"
              rel="noreferrer"
              data-testid="footer-video-consult"
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#EB8771] hover:bg-[#E2725C] text-white px-5 py-2.5 text-sm font-medium transition-all hover:scale-[1.03]"
            >
              Book Video Consultation
            </a>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <p className="text-xs text-[#E8F0E9]/60">
            © {new Date().getFullYear()} Asian Diabetes Thyroid Hormone Superspeciality Centre.
            All rights reserved.
          </p>
          <p className="text-[11px] text-[#E8F0E9]/50 max-w-xl md:text-right">
            Educational information only — not a substitute for professional medical advice.
            Treatment decisions are made after individual consultation.
          </p>
        </div>
      </div>
    </footer>
  );
};
